'use client';

import React, { useMemo } from 'react';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import {
  useGetCodeSetVolumeByCodeQuery,
  useGetCodeByNameQuery,
} from 'src/api/redux';
import { applyDisplayMode } from 'src/components/Profiles/shared/widgets/utils';

const PREVIEW_MAX_ITEMS = 2;

const CompactCodeSetVolumeByCodePreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const entityId = entityDetails?.id;
  const codeSetId = widgetConfig?.widget?.code_set?.id;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;

  const { data, isLoading } = useGetCodeSetVolumeByCodeQuery(
    {
      entityId: String(entityId ?? ''),
      codeSetId: codeSetId!,
      minYear,
      maxYear,
    },
    { skip: !entityId || !codeSetId }
  );

  const topItems = useMemo(() => {
    if (!data?.results?.length) return [];
    return data.results.slice(0, PREVIEW_MAX_ITEMS);
  }, [data]);

  const codeNames = useMemo(() => {
    return topItems.map((item) => item.code_name);
  }, [topItems]);

  const { data: codeDetailsData } = useGetCodeByNameQuery(
    {
      codeIds: codeNames,
    },
    {
      skip: codeNames.length === 0,
    }
  );

  const descriptionMap = useMemo(() => {
    if (!codeDetailsData?.results) return {};
    return codeDetailsData.results.reduce(
      (
        acc: Record<string, string>,
        code: { code_name?: string; description?: string }
      ) => {
        if (code.code_name && code.description) {
          acc[code.code_name] = code.description;
        }
        return acc;
      },
      {}
    );
  }, [codeDetailsData]);

  const displayMode = widgetConfig?.widget?.display_mode;

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="compact-code-set-volume-by-code-preview-loading" aria-label="Loading code set volume" />;
  }

  if (topItems.length === 0) {
    return <WidgetEmpty data-testid="compact-code-set-volume-by-code-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="compact-code-set-volume-by-code-preview"
    >
      <div className="flex items-center justify-end gap-2 px-1 pb-1 border-b border-neutral-100">
        <span className="text-xs font-semibold text-primary min-w-16 text-end">
          Claims
        </span>
        <span className="text-xs font-semibold text-primary min-w-16 text-end">
          Patients
        </span>
      </div>
      {topItems.map((item) => (
        <div
          key={item.code_name}
          className="flex items-center justify-between gap-2 py-2 border-b border-neutral-100 last:border-b-0"
          data-testid="compact-code-set-volume-by-code-preview-row"
        >
          <div className="flex flex-col min-w-0 flex-1">
            <span
              className="text-sm font-bold text-primary truncate"
              title={item.code_name}
            >
              {item.code_name}
            </span>
            {descriptionMap[item.code_name] && (
              <span
                className="text-xs text-secondary truncate"
                title={descriptionMap[item.code_name]}
                data-testid="compact-code-set-volume-by-code-preview-description"
              >
                {descriptionMap[item.code_name]}
              </span>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <div className="flex items-center justify-end px-2 py-1 min-w-16">
              <span className="text-sm font-semibold text-teal-900 tabular-nums">
                {applyDisplayMode(item.claims, displayMode).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-end px-2 py-1 min-w-16">
              <span className="text-sm font-semibold text-teal-900 tabular-nums">
                {applyDisplayMode(
                  item.patients,
                  displayMode
                ).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompactCodeSetVolumeByCodePreview;
